import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoStorefrontOutline } from 'react-icons/io5';
import { HiOutlineShoppingBag, HiOutlineHeart, HiOutlineUser, HiOutlineMenu, HiX } from 'react-icons/hi';
import { ProductContext } from '../context/ProductContext';

const NavBadge = ({ count }) => {
  if (!count) return null;
  return (
    <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white">
      {count}
    </span>
  );
};

const Navbar = () => {
  const { cartItem, wishlist, filters, setFilters } = useContext(ProductContext);
  const [query, setQuery] = useState(filters.search || '');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query !== filters.search) {
        setFilters({ search: query, category: null });
        if (query && location.pathname !== '/') navigate('/');
      }
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const cartCount = cartItem.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-1.5 text-brand-700">
          <IoStorefrontOutline size={24} />
          <span className="text-lg font-bold tracking-tight">ShopSmart</span>
        </Link>

        <div className="hidden flex-1 sm:block">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products…"
            aria-label="Search"
            className="w-full max-w-md rounded-full border border-gray-300 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <div className="ml-auto hidden items-center gap-5 sm:flex">
          <Link to="/login" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-brand-700">
            <HiOutlineUser size={20} /> Login
          </Link>
          <Link to="/wishlist" className="relative text-gray-700 hover:text-brand-700">
            <HiOutlineHeart size={22} />
            <NavBadge count={wishlist.length} />
          </Link>
          <Link to="/cart" className="relative text-gray-700 hover:text-brand-700">
            <HiOutlineShoppingBag size={22} />
            <NavBadge count={cartCount} />
          </Link>
        </div>

        <button
          className="ml-auto flex items-center gap-3 sm:hidden"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <Link to="/cart" className="relative text-gray-700">
            <HiOutlineShoppingBag size={22} />
            <NavBadge count={cartCount} />
          </Link>
          {menuOpen ? <HiX size={24} /> : <HiOutlineMenu size={24} />}
        </button>
      </div>

      <div className="px-4 pb-3 sm:hidden">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products…"
          aria-label="Search"
          className="w-full rounded-full border border-gray-300 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>

      {menuOpen && (
        <div className="flex flex-col gap-1 border-t border-gray-100 px-4 py-3 sm:hidden">
          <Link to="/login" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Login
          </Link>
          <Link to="/wishlist" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Wishlist ({wishlist.length})
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
